import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {LeaderboardType} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {IRankingsApiProvider} from "data/providers/api/rankings.api.provider";
import type {IRanking} from "data/types/rankings";

export interface IRankingsStore {
	get isLoading(): boolean;

	get lastUpdated(): Date | null;

	get activeLeaderboardType(): LeaderboardType;

	set activeLeaderboardType(value: LeaderboardType);

	get rankings(): IRanking[];

	get userRankings(): IRanking | null;

	get nextPage(): boolean;

	clearRankings(): void;

	fetchContestRankings(contestId: number): Promise<void>;

	fetchMoreContestRankings(contestId: number): Promise<void>;

	fetchRoundRankings(roundId: number): Promise<void>;

	fetchMoreRoundRankings(roundId: number): Promise<void>;
	updateLastUpdated(date: Date | null): void;
}

@injectable()
export class RankingsStore implements IRankingsStore {
	@observable private _isLoading: boolean = false;
	@observable private _lastUpdated: Date | null = null;
	@observable private _activeLeaderboardType: LeaderboardType = LeaderboardType.Daily;
	@observable private _rankings: IRanking[] = [];
	@observable private _userRankings: IRanking | null = null;
	@observable private _nextPage: boolean = false;
	@observable private _page: number = 1;

	constructor(@inject(Bindings.RankingsApiProvider) private _apiProvider: IRankingsApiProvider) {
		makeAutoObservable(this);
	}

	get lastUpdated(): Date | null {
		return this._lastUpdated;
	}

	get nextPage(): boolean {
		return this._nextPage;
	}

	get userRankings(): IRanking | null {
		return this._userRankings;
	}

	get rankings(): IRanking[] {
		return this._rankings;
	}

	get activeLeaderboardType(): LeaderboardType {
		return this._activeLeaderboardType;
	}

	set activeLeaderboardType(value: LeaderboardType) {
		this._page = 1;
		this._activeLeaderboardType = value;
	}

	get isLoading(): boolean {
		return this._isLoading;
	}

	private get requestPayload() {
		return {
			page: this._page,
		};
	}

	@action
	public async fetchContestRankings(contestId: number): Promise<void> {
		try {
			this._isLoading = true;
			const {data} = await this._apiProvider.contestRankings({
				contestId,
				...this.requestPayload,
			});
			runInAction(() => {
				this._page = 1;
				this._rankings = data.success.rankings;
				this._userRankings = data.success.userRanking;
				this._nextPage = data.success.nextPage;
				this._lastUpdated = null;
			});
		} catch (e) {
			return Promise.reject(e);
		} finally {
			runInAction(() => {
				this._isLoading = false;
			});
		}
	}

	@action
	public async fetchMoreContestRankings(contestId: number): Promise<void> {
		try {
			this._page += 1;
			this._isLoading = true;
			const {data} = await this._apiProvider.contestRankings({
				contestId,
				...this.requestPayload,
			});

			runInAction(() => {
				this._rankings = [...this.rankings, ...data.success.rankings];
				this._userRankings = data.success.userRanking;
				this._nextPage = data.success.nextPage;
			});
		} catch (e) {
			return Promise.reject(e);
		} finally {
			runInAction(() => {
				this._isLoading = false;
			});
		}
	}

	@action
	public async fetchRoundRankings(roundId: number): Promise<void> {
		try {
			this._isLoading = true;
			const {data} = await this._apiProvider.roundRankings({roundId, ...this.requestPayload});
			runInAction(() => {
				this._page = 1;
				this._rankings = data.success.rankings;
				this._userRankings = data.success.userRanking;
				this._nextPage = data.success.nextPage;
				this._lastUpdated = null;
			});
		} catch (e) {
			return Promise.reject(e);
		} finally {
			runInAction(() => {
				this._isLoading = false;
			});
		}
	}

	public async fetchMoreRoundRankings(roundId: number): Promise<void> {
		try {
			this._isLoading = true;
			this._page += 1;
			const {data} = await this._apiProvider.roundRankings({roundId, ...this.requestPayload});
			runInAction(() => {
				this._rankings = [...this.rankings, ...data.success.rankings];
				this._userRankings = data.success.userRanking;
				this._nextPage = data.success.nextPage;
			});
		} catch (e) {
			return Promise.reject(e);
		} finally {
			runInAction(() => {
				this._isLoading = false;
			});
		}
	}

	updateLastUpdated(date: Date | null) {
		this._lastUpdated = date;
	}

	@action
	public clearRankings() {
		this._rankings = [];
		this._userRankings = null;
		this._nextPage = false;
		this._page = 1;
		this._lastUpdated = null;
	}
}
